import { useQuery } from '@tanstack/react-query';

import { useTranslation } from 'react-i18next';
import { getHealth, type HealthResponse } from '@/shared/api/health';

import styles from './StatusPage.module.css';

/**
 * Backend status page.
 *
 * It calls the aggregate health endpoint on purpose: that single request
 * exercises the whole chain — browser, nginx, gateway and all three domain
 * services over gRPC — so a broken link shows up on screen instead of in a
 * debugging session.
 */
export function StatusPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { data, isPending, isError, error } = useQuery<HealthResponse>({
    queryKey: ['health'],
    queryFn: ({ signal }) => getHealth(signal),
  });

  return (
    <section className={styles.page}>
      <h1 className={styles.title}>{t('brand')}</h1>
      <p className={styles.subtitle}>{t('tagline')}</p>

      <section aria-labelledby="backend-state">
        <h2 className={styles.sectionTitle} id="backend-state">
          {t('status.title')}
        </h2>

        {isPending && <p>{t('status.checking')}</p>}

        {isError && (
          <p className={styles.failed} role="alert">
            {t('status.down', { message: error.message })}
          </p>
        )}

        {data && (
          <ul className={styles.services}>
            {data.services.map((service) => (
              <li className={styles.service} key={service.name}>
                <span>{service.name}</span>
                <span
                  className={service.status === 'serving' ? styles.ok : styles.failed}
                  title={service.detail}
                >
                  {service.status === 'serving' ? t('status.up') : t('status.failing')}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </section>
  );
}
